import { useState, useEffect, useRef } from 'react';
import { COLORS } from '../lib/colors';
import { formatArticleDate } from '../lib/format';

// Cabecera de sección: Space Mono 11/2.2px para el eyebrow y Playfair 400 con
// tracking negativo para el titular, medidos de la referencia.
const SectionHeader = ({ eyebrow, titulo }) => (
  <div style={{ marginBottom: 48 }}>
    <p style={{
      fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 400,
      letterSpacing: "2.2px", textTransform: "uppercase",
      color: COLORS.gold, margin: "0 0 16px",
    }}>
      {eyebrow}
    </p>
    <h2 style={{
      fontFamily: "'Playfair Display', serif",
      fontSize: "clamp(30px, 5vw, 52px)", fontWeight: 400, letterSpacing: "-0.01em",
      color: COLORS.cream,
      lineHeight: 1.1, margin: 0, maxWidth: 720,
    }}>
      {titulo}
    </h2>
  </div>
);

// sr-only: visible solo para lectores de pantalla (aria-live de estado).
const srOnlyStyle = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
  overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0,
};

// La referencia trae aquí 3 tarjetas estáticas inventadas. Esta versión es la
// nuestra: "Conocimiento del foro" muestra TODOS los artículos reales de la
// base de datos, paginados. Las 3 imágenes de la referencia se reciclan por
// posición (i % 3) porque la base no tiene imagen por artículo.
const IMAGENES_TARJETA = ["knowledge-succession", "knowledge-protocol", "knowledge-council"];

export default function KnowledgeSection({ openArticle }) {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 24, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const firstNewIdRef = useRef(null);
  const newItemRef = useRef(null);

  useEffect(() => {
    fetch("/api/articles.php?per_page=24")
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setArticles(data.data);
          if (data.pagination) setPagination(data.pagination);
        } else {
          setError("Error al cargar artículos.");
        }
      })
      .catch(() => setError("No se pudieron cargar los artículos."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (firstNewIdRef.current && newItemRef.current) {
      newItemRef.current.focus();
      firstNewIdRef.current = null;
    }
  }, [articles]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    const nextPage = (pagination.page || 1) + 1;
    fetch(`/api/articles.php?page=${nextPage}&per_page=24`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          firstNewIdRef.current = data.data[0] ? data.data[0].id : null;
          setArticles(prev => [...new Map([...prev, ...data.data].map(x => [x.id, x])).values()]);
          if (data.pagination) setPagination(data.pagination);
        } else {
          setError("Error al cargar artículos.");
        }
      })
      .catch(() => setError("No se pudieron cargar los artículos."))
      .finally(() => setLoadingMore(false));
  };

  const sorted = [...articles].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
  const hasMore = pagination.total > articles.length;

  return (
    // Sin scrollMarginTop: el hueco bajo el header fijo lo pone el `offset: -80`
    // de Lenis, igual que en el resto de secciones. Tenerlo aquí lo sumaba dos
    // veces y la sección quedaba 96px más abajo que las demás.
    <section id="conocimiento" style={{ padding: "clamp(64px, 12vw, 160px) clamp(20px, 8vw, 128px)", background: COLORS.navy }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ position: "relative", display: "flow-root" }}>
          <SectionHeader eyebrow="Reflexiones recientes" titulo="Conocimiento del foro" />
          {/* Filete decorativo de la referencia: 80x1px, dorado al 30%, oculto en
              móvil. bottom:64 = 48 (marginBottom propio de SectionHeader) + 16 (mb-4
              medido en la referencia sobre el pie del bloque de título). Requiere que
              el wrapper sea flow-root para que el margen de 48 cuente dentro de su
              propia caja y el cálculo de "bottom" sea estable. */}
          <div className="todd-know-divider" style={{ position: "absolute", right: 0, bottom: 64, width: 80, height: 1, background: "rgba(201, 162, 110, 0.3)" }} />
        </div>

        {/* Tarjetas: TODOS los articulos reales. Sin etiqueta de categoria,
            porque esa columna no existe en la base de datos y no se inventa;
            en su lugar va la fecha. */}
        {loading && (
          <div style={{ minHeight: 320 }}>
            <p aria-live="polite" style={{ fontFamily: "'Inter', sans-serif", color: COLORS.mutedLight, padding: "28px 0" }}>
              Cargando artículos…
            </p>
          </div>
        )}

        {error && (
          <p role="alert" style={{ fontFamily: "'Inter', sans-serif", color: COLORS.error, padding: "28px 0" }}>
            {error}
          </p>
        )}

        {!loading && !error && sorted.length === 0 && (
          <div style={{ border: `1px dashed ${COLORS.muted}`, padding: "48px 32px", textAlign: "center", marginTop: 48 }}>
            <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 20, color: COLORS.mutedLight, margin: 0 }}>
              Pronto compartiremos nuevas reflexiones.
            </p>
          </div>
        )}

        {!loading && !error && sorted.length > 0 && (
          <>
            <div className="todd-know-grid">
              {sorted.map((a, i) => (
                <a
                  key={a.id}
                  href={`/conocimiento/${a.slug}`}
                  className="todd-know-card"
                  onClick={(ev) => { ev.preventDefault(); openArticle(a.slug); }}
                >
                  <div className="todd-know-img">
                    <img
                      src={`/assets/${IMAGENES_TARJETA[i % IMAGENES_TARJETA.length]}.jpg`}
                      alt=""
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  </div>
                  <p style={{
                    fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "1.5px",
                    textTransform: "uppercase", color: COLORS.gold, margin: "20px 0 12px",
                  }}>
                    {formatArticleDate(a.published_at)}
                  </p>
                  <h3 className="todd-know-title" style={{
                    fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 400,
                    lineHeight: 1.3, color: COLORS.creamText, margin: "0 0 16px",
                  }}>
                    {a.title}
                  </h3>
                  {a.excerpt && (
                    <p style={{
                      fontFamily: "'Inter', sans-serif", fontSize: 14, lineHeight: 1.8,
                      color: COLORS.muted, margin: "0 0 16px",
                    }}>
                      {a.excerpt}
                    </p>
                  )}
                  <span style={{
                    fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500,
                    letterSpacing: "1.2px", textTransform: "uppercase", color: COLORS.gold,
                  }}>
                    Leer más →
                  </span>
                </a>
              ))}
            </div>

            {hasMore && (
              <div style={{ textAlign: "center", marginTop: 56 }}>
                <button
                  className="todd-loadmore"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{
                    fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: "1.2px",
                    textTransform: "uppercase", color: COLORS.gold, background: "transparent",
                    border: `1px solid ${COLORS.gold}66`, padding: "0 32px", lineHeight: "44px",
                    cursor: loadingMore ? "default" : "pointer", transition: "background 0.2s, color 0.2s",
                  }}
                >
                  {loadingMore ? "CARGANDO…" : "VER MÁS ARTÍCULOS"}
                </button>
              </div>
            )}

            <div aria-live="polite" style={srOnlyStyle}>
              {loadingMore
                ? "Cargando más artículos…"
                : (!hasMore && articles.length > 0 ? `Mostrando ${articles.length} de ${pagination.total} artículos.` : "")}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
